/**
 * The coverage map: where this product has data, and where it does not.
 *
 * Public, like the legal pages and `/api/counties` itself. A coverage claim
 * only a subscriber can check is not a claim worth much, and a prospect asking
 * "do you have my county?" deserves an answer before a signup form.
 *
 * The page shows two things that are deliberately not merged: the registry's
 * *status* (what we have built) and the last refresh's *counts* (what we
 * actually hold). See backend/app/api/routes/counties.py.
 */

import { useEffect, useMemo, useState } from "react";

import { ApiError, api } from "../api/client";
import type { County, Jurisdiction } from "../api/types";
import { isNamed, jurisdictionLabel, namedFirst } from "../lib/jurisdiction";
import { useAuth } from "../auth/AuthProvider";
import { Link, navigate, useSearchParam } from "../router";
import { FloridaMap } from "./FloridaMap";
import { Footer } from "./Footer";
import { fmt } from "../lib/format";
import { countyDepth } from "../lib/countyDepth";
import { hasSourceWarning, sourceLine, sourceWarning } from "../lib/sources";
import { COVERAGE_PATH, DASHBOARD_PATH, SIGNIN_PATH } from "../lib/route";

export function CoveragePage() {
  const { user } = useAuth();
  const selected = useSearchParam("county");
  const [counties, setCounties] = useState<County[]>([]);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    api
      .counties(controller.signal)
      .then((response) => {
        setCounties(response.counties);
        setNote(response.note);
      })
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(
          cause instanceof ApiError ? cause.message : "Couldn't load coverage.",
        );
      });
    return () => controller.abort();
  }, []);

  const byFips = useMemo(
    () => Object.fromEntries(counties.map((county) => [county.fips, county])),
    [counties],
  );
  const live = counties.filter((county) => county.status === "live");
  const current = byFips[selected];

  return (
    <>
      <header className="topbar">
        <Link to="/" className="wordmark">
          <span>D</span>is<span>TR</span>ess Signal
        </Link>
        <div className="loc hide-sm">Coverage</div>
        <div className="spacer" />
        <Link to={user ? DASHBOARD_PATH : SIGNIN_PATH} className="linkbtn">
          {user ? "All counties" : "Sign in"}
        </Link>
      </header>

      <div className="coverage">
        <div className="eyebrow">Florida · {live.length} of 67 counties</div>
        <h1 className="display">Where the data is</h1>
        {/* Signed out this page is the front door, so it has to say what the
            product is before it says how much of Florida it holds. */}
        {!user && (
          <p className="lede coverage-lede">
            Structural distress in Florida, pulled from county records —
            buildings overdue for milestone inspection that are already carrying
            open violations. Pick a county to see what we hold.
          </p>
        )}
        <p className="sub coverage-note">{note}</p>

        {error && (
          <div className="err" role="alert">
            {error}
          </div>
        )}

        <div className="coverage-grid">
          <div className="coverage-map">
            <FloridaMap
              counties={byFips}
              selected={selected}
              onSelect={(fips) => navigate(`${COVERAGE_PATH}?county=${fips}`)}
            />
            <ul className="legend">
              <li>
                <span className="swatch is-live" /> Covered
              </li>
              <li>
                <span className="swatch is-planned" /> In progress
              </li>
              <li>
                <span className="swatch is-not_covered" /> No source wired
              </li>
            </ul>
          </div>

          <CountyPanel county={current} live={live} signedIn={Boolean(user)} />
        </div>
      </div>

      <Footer agreementVersion={user?.legal.current_version} />
    </>
  );
}

// ---------------------------------------------------------------------------
// The detail panel
// ---------------------------------------------------------------------------

function CountyPanel({
  county,
  live,
  signedIn,
}: {
  county?: County;
  live: County[];
  signedIn: boolean;
}) {
  if (!county) {
    const only = live.length === 1 ? live[0] : undefined;
    return (
      <aside className="county-panel">
        <div className="eyebrow">Pick a county</div>
        <p className="sub">
          {only
            ? `${only.name} is the county with data behind it today. Every
               other county is drawn so you can see what is missing rather than
               guess at it.`
            : "Select a shaded county to see what the last refresh returned."}
        </p>
        {/* A grid rather than a stack: one full-width button per county was a
            short list at four counties and a column taller than the map at
            twenty-one. The list itself stays — the map is no way in from a
            keyboard, and a hard one on a phone. The " County" suffix is
            dropped so the longest name still fits two to a row; the eyebrow
            above already says what these are. */}
        <div className="county-list">
          {live.map((entry) => (
            <button
              key={entry.fips}
              className="btn county-pick"
              title={`${entry.name} County`}
              onClick={() => navigate(`${COVERAGE_PATH}?county=${entry.fips}`)}
            >
              {entry.name}
            </button>
          ))}
        </div>
      </aside>
    );
  }

  return (
    <aside className="county-panel">
      <div className="eyebrow">{STATUS_LABEL[county.status]}</div>
      <h2 className="county-name">{county.name} County</h2>

      {county.status === "live" ? (
        <>
          <dl className="county-stats">
            <div>
              <dt>Parcels</dt>
              <dd>{fmt(county.parcels)}</dd>
            </div>
            <div>
              <dt>Carrying a signal</dt>
              <dd>{fmt(county.scored)}</dd>
            </div>
            <div>
              <dt>Buildings</dt>
              <dd>{fmt(county.buildings)}</dd>
            </div>
            <div>
              <dt>Milestone overdue</dt>
              <dd>{fmt(county.milestone_overdue)}</dd>
            </div>
          </dl>

          {/* Depth, under the volume. The four figures above say how much is
              here; none of them says what kind, and a county can hold 27,000
              parcels and no code enforcement record at all. This is the public
              page a visitor decides on before they have an account, so it is
              the earliest honest place to say it. Derived from the served
              signal list — see lib/countyDepth.ts. */}
          <p className="county-depth">{countyDepth(county.signals).summary}</p>

          {/* And how old what is here actually is. Depth says whether the
              county publishes a case register; this says whether anyone is
              still writing to it. Fort Lauderdale's stopped in 2019 and went
              on serving thousands of cases marked open, and this panel — the
              page somebody decides on — showed the same four figures and the
              same signal list as a county whose register is current. See
              lib/sources.ts for the wording rules. */}
          {hasSourceWarning(county.sources) && (
            <p className="county-sources">
              <strong>How current this is:</strong>{" "}
              {sourceWarning(county.sources)}
            </p>
          )}
          {county.sources.length > 0 && (
            <ul className="source-ages">
              {county.sources.map((source) => (
                <li key={source.label} className={source.stale ? "stale" : ""}>
                  {sourceLine(source)}
                </li>
              ))}
            </ul>
          )}

          {/* The same link either way — the county rides in the URL through
              sign-in, so a visitor lands on the county they clicked rather
              than on a generic dashboard. Only the promise changes. */}
          <Link
            to={
              signedIn
                ? `${DASHBOARD_PATH}?county=${county.fips}`
                : `${SIGNIN_PATH}?county=${county.fips}`
            }
            className="btn btn-link"
          >
            {signedIn ? `Open ${county.name} →` : `Sign in to open ${county.name} →`}
          </Link>
          {!signedIn && (
            <p className="sub gate-hint">
              The counts above are free and need no account. A free account adds
              the ten worst buildings; parcel-level records, filters and saved
              alerts start on a paid plan.
            </p>
          )}

          <Municipalities fips={county.fips} name={county.name} />
        </>
      ) : (
        <p className="sub">
          No source is wired for {county.name} yet, so this map shows nothing
          here. That is a statement about our coverage — not about whether{" "}
          {county.name} has distressed buildings.
        </p>
      )}
    </aside>
  );
}

const STATUS_LABEL: Record<County["status"], string> = {
  live: "Covered",
  planned: "In progress",
  not_covered: "Not covered",
};

/**
 * The municipal breakdown inside a covered county — the same numbers
 * `/api/coverage` has always published, now scoped to the county you clicked.
 */
function Municipalities({ fips, name }: { fips: string; name: string }) {
  const [rows, setRows] = useState<Jurisdiction[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    api
      .coverage(fips, controller.signal)
      .then((response) => setRows(response.jurisdictions))
      .catch(() => setRows([]));
    return () => controller.abort();
  }, [fips]);

  if (!rows) return <p className="sub">Loading {name}'s municipalities…</p>;
  if (rows.length === 0) return null;

  return (
    <div className="county-municipalities">
      <div className="eyebrow">Within {name}</div>
      <table className="mini-table">
        <thead>
          <tr>
            <th>Jurisdiction</th>
            <th className="num">Parcels</th>
          </tr>
        </thead>
        <tbody>
          {namedFirst(rows).slice(0, 12).map((row) => (
            <tr key={row.municipality || "unnamed"}>
              <td className={isNamed(row) ? undefined : "sub"}>
                {jurisdictionLabel(row.municipality)}
              </td>
              <td className="num">{fmt(row.parcels)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sub">
        Each municipality also runs its own code compliance, and those cases are
        not in these counts. A low number is a statement about our sources.
      </p>
    </div>
  );
}
